import { motion } from 'framer-motion';
import { ArrowUpRight, Users, Clock, Sparkles, Construction } from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const CLD = 'https://res.cloudinary.com/amenitiz/image/upload/q_auto,f_auto,w_1200';

const EVENTS = [
  {
    id: 'mariage',
    title: 'Mariages',
    badge: 'Celebration',
    capacity: 150,
    capacityLabel: 'invites',
    tagline: 'jusqu\'a 150 invites · ceremonie en jardin',
    duration: 'Weekend complet',
    description: 'Celebrez votre union dans un cadre enchanteur. Salle de reception, jardin pour la ceremonie, gite pour vos proches.',
    image: `${CLD}/v1695039520/hwkntar3eqd4nuke3pol.jpg`,
  },
  {
    id: 'anniversaire',
    title: 'Anniversaires',
    badge: 'Reception',
    capacity: 150,
    capacityLabel: 'invites',
    tagline: 'jusqu\'a 150 invites · piste de danse',
    duration: 'Journee ou soiree',
    description: 'Des anniversaires intimes aux grandes celebrations. Salle des fetes et gite a votre disposition pour vos plus belles fetes.',
    image: `${CLD}/v1695040682/sntcpo84gwhmazbvihyt.jpg`,
  },
  {
    id: 'seminaire',
    title: 'Seminaires',
    badge: 'Corporate',
    capacity: 150,
    capacityLabel: 'participants',
    tagline: 'jusqu\'a 150 participants · wifi haut debit + paperboard',
    duration: '1 a 3 jours',
    description: 'Un cadre inspire pour vos reunions d\'equipe et conferences. Salle modulable, hebergement sur place, cuisine equipee, wifi haut debit.',
    image: `${CLD}/v1695308948/uydhsmvdigbmpifxlky6.jpg`,
  },
  {
    id: 'famille',
    title: 'Repas de famille',
    badge: 'Intime',
    capacity: 20,
    capacityLabel: 'convives',
    tagline: 'jusqu\'a 20 convives · gite complet privatise',
    duration: 'Journee ou weekend',
    description: 'Retrouvailles, fetes de fin d\'annee, cousinades. Reunissez vos proches dans le gite complet et son espace exterieur convivial.',
    image: `${CLD}/v1695308676/g1fcx3r8wctxfsvx3bog.jpg`,
  },
];

const easeLuxe = [0.16, 1, 0.3, 1];

export default function Events() {
  const featureFlags = useFeatureFlags();
  const scrollToReservation = () => {
    const el = document.querySelector('#reservation');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="evenements"
      data-testid="events-section"
      className="py-28 md:py-40 bg-[#EFECE7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.5 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="mb-20 md:mb-28 max-w-3xl"
        >
          <motion.span
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeLuxe } } }}
            className="overline"
          >
            Agenda du domaine
          </motion.span>
          <motion.h2
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
            data-testid="events-title"
            className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-[#1A1C19] mt-5 tracking-[-0.025em] leading-[0.95]"
          >
            Vos
            <span className="italic text-[#5A6153]"> celebrations</span>
          </motion.h2>
          <motion.p
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeLuxe } } }}
            className="mt-8 text-[#5A6153] max-w-xl text-base md:text-lg leading-relaxed font-light"
          >
            Mariages, seminaires, anniversaires ou retrouvailles &mdash; chaque evenement
            trouve son ecrin dans nos 1 200 m&sup2; d&apos;hospitalite.
          </motion.p>
        </motion.div>

        {/* Horizontal cards - alternating */}
        <div className="space-y-8 md:space-y-12">
          {!featureFlags.gite_salle_enabled && (
            <motion.div
              data-testid="events-coming-soon"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, ease: easeLuxe }}
              className="bg-white rounded-[1.5rem] md:rounded-[2rem] p-10 md:p-16 text-center shadow-[0_8px_30px_rgba(26,28,25,0.04)]"
            >
              <Construction className="w-10 h-10 md:w-12 md:h-12 text-[#CBA77A] mx-auto mb-6" strokeWidth={1.2} />
              <span className="overline block mb-4">Bientot disponible</span>
              <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-[#1A1C19] tracking-[-0.02em] leading-[1.1] mb-5">
                Notre <em className="italic">salle des fetes</em> est en cours d'amenagement
              </h3>
              <p className="text-[#5A6153] text-sm md:text-base leading-relaxed font-light max-w-2xl mx-auto">
                Mariages, anniversaires, seminaires et repas de famille ouvriront a la reservation prochainement.
                D'ici la, nos hebergements restent ouverts pour vos sejours en gite ou en appartement.
              </p>
              <button
                type="button"
                onClick={scrollToReservation}
                data-testid="events-fallback-cta"
                className="mt-8 inline-flex items-center gap-2 text-[#1A1C19] hover:text-[#CBA77A] text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 pb-1 border-b border-[#1A1C19]/30 hover:border-[#CBA77A]"
              >
                Reserver un sejour
                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </motion.div>
          )}
          {featureFlags.gite_salle_enabled && EVENTS.map((ev, i) => {
            const imageRight = i % 2 === 1;
            return (
              <motion.article
                key={ev.id}
                data-testid={`event-card-${ev.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: easeLuxe }}
                className="group grid grid-cols-1 md:grid-cols-12 gap-0 bg-white rounded-[1.5rem] md:rounded-[2rem] overflow-hidden shadow-[0_8px_30px_rgba(26,28,25,0.04)] hover:shadow-[0_24px_60px_-12px_rgba(26,28,25,0.18)] transition-all duration-700"
              >
                {/* Image */}
                <button
                  type="button"
                  onClick={scrollToReservation}
                  aria-label={`Reserver - ${ev.title}`}
                  className={`relative w-full h-72 md:h-[420px] overflow-hidden md:col-span-7 ${imageRight ? 'md:order-2' : ''}`}
                >
                  <img
                    src={ev.image}
                    alt={ev.title}
                    className="w-full h-full object-cover card-image-zoom"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                  {/* Badge glassmorphism */}
                  <div className="absolute top-5 left-5 md:top-7 md:left-7">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase bg-white/85 backdrop-blur-md text-[#1A1C19]">
                      <Sparkles className="w-3 h-3 text-[#CBA77A]" strokeWidth={2} />
                      {ev.badge}
                    </span>
                  </div>
                </button>

                {/* Content side */}
                <div className={`p-8 md:p-12 lg:p-14 flex flex-col justify-between md:col-span-5 ${imageRight ? 'md:order-1' : ''}`}>
                  {/* Top: "Agenda" gold capacity display */}
                  <div>
                    <div className="flex items-baseline gap-4 mb-3">
                      <span className="font-['Cormorant_Garamond'] text-7xl md:text-8xl font-light text-[#CBA77A] tracking-[-0.03em] leading-none">
                        {ev.capacity}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.28em] text-[#5A6153] font-semibold pb-2">
                        {ev.capacityLabel}
                        <br />
                        max
                      </span>
                    </div>

                    <p
                      data-testid={`event-tagline-${ev.id}`}
                      className="text-[12px] md:text-[13px] italic font-light text-[#5A6153]/85 tracking-wide mb-7"
                    >
                      {ev.tagline}
                    </p>

                    <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl lg:text-5xl font-light text-[#1A1C19] tracking-[-0.02em] leading-[1.05] mb-5">
                      {ev.title}
                    </h3>

                    <p className="text-[#5A6153] text-sm md:text-[15px] leading-relaxed font-light mb-7">
                      {ev.description}
                    </p>

                    {/* Specs row */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[#1A1C19]/65 text-xs border-t border-black/[0.06] pt-5">
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-[#CBA77A]" strokeWidth={1.5} />
                        <span className="tracking-wide">{ev.capacity} {ev.capacityLabel}</span>
                      </div>
                      <span className="w-px h-3 bg-black/15" />
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-[#CBA77A]" strokeWidth={1.5} />
                        <span className="tracking-wide">{ev.duration}</span>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    type="button"
                    data-testid={`event-reserve-${ev.id}`}
                    onClick={scrollToReservation}
                    className="group/btn mt-9 inline-flex items-center gap-2 text-[#1A1C19] hover:text-[#CBA77A] text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-300 pb-1 border-b border-[#1A1C19]/30 hover:border-[#CBA77A] self-start"
                  >
                    <span>Organiser cet evenement</span>
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" strokeWidth={2} />
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
